import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex items-center gap-x-2 text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)" };
const _hoisted_2 = { class: "flex flex-col gap-y-5" };
const _hoisted_3 = { class: "flex w-full items-center justify-between" };
const _hoisted_4 = { class: "flex items-center gap-4" };
import { AutoComplete, Button, Modal, SelectInput, ToggleInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAppToast } from "@/shared/composables/useAppToast";
import { applyParsedAddress, parseAddressQuery, seedAddressDetails, } from "@/modules/organisation/domain/address-utils";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
import { defaultAddressCountry, requiredAddressFieldsFilled, toAddressLayout, } from "@/shared/utils/addressFieldConfig";
import { useAddressSearch, } from "@/modules/organisation/composables/useAddressSearch";
import { pinSuggestionPoint, pointForFields, } from "@/modules/organisation/public";
import AddressDetailsModal from "@/modules/organisation/presentation/components/AddressDetailsModal.vue";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { updateContactAddress } from "../../infrastructure/adapters/contactService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactEditAddressFormModal',
    props: {
        modelValue: { type: Boolean },
        contactId: {},
        initialAddress: {}
    },
    emits: ["update:modelValue", "updated", "delete"],
    setup(__props, { emit: __emit }) {
        const ADDRESS_TYPE_OPTIONS = ["Home", "Work", "Billing", "Shipping", "Other"];
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const addressFormatter = useAddressFormatter();
        const workspaceCountry = useWorkspaceCountry();
        const defaultCountry = computed(() => defaultAddressCountry(workspaceCountry.value));
        const { toastError, toastSuccess } = useAppToast();
        const ERROR_OVERRIDES = {};
        const typeOptions = computed(() => ADDRESS_TYPE_OPTIONS.map((type) => ({
            value: type,
            label: t(`contact.detail.addresses.types.${type}`),
        })));
        // ---------------------------------------------------------------------------
        // Local form state
        // ---------------------------------------------------------------------------
        const isSaving = ref(false);
        const isPrimary = ref(false);
        const selectedType = ref("Other");
        const addressQuery = ref("");
        const seededAddressQuery = ref("");
        const addressReplaced = ref(false);
        const pinnedPoint = ref(null);
        const form = reactive({
            line1: "",
            line2: "",
            line3: "",
            houseNumber: "",
            houseNumberSuffix: "",
            postalCode: "",
            city: "",
            region: "",
            country: "",
        });
        function seedFromInitial() {
            const a = props.initialAddress;
            form.line1 = a.line1 ?? "";
            form.line2 = a.line2 ?? "";
            form.line3 = a.line3 ?? "";
            form.houseNumber = a.houseNumber ?? "";
            form.houseNumberSuffix = a.houseNumberSuffix ?? "";
            form.postalCode = a.postalCode ?? "";
            form.city = a.city ?? "";
            form.region = a.region ?? "";
            form.country = a.country || defaultCountry.value;
            isPrimary.value = a.isPrimary;
            selectedType.value = a.type === "Unknown" ? "Other" : a.type;
            addressQuery.value = addressFormatter.format(a);
            seededAddressQuery.value = addressQuery.value;
            addressReplaced.value = false;
            pinnedPoint.value = null;
            showAddressDetailsModal.value = false;
        }
        function resetForm() {
            isSaving.value = false;
            seedFromInitial();
        }
        // ---------------------------------------------------------------------------
        // Address autocomplete
        // ---------------------------------------------------------------------------
        const { suggestions: addressSuggestions, loading: addressLoading, search: _onAddressSearch, } = useAddressSearch(3); // 3-char floor: matches the BFF SearchAdresseQueryValidator (MinimumLength(3)) and the upstream BAN API, which both reject shorter queries (OP-6112)
        function onAddressSearch(query) {
            addressQuery.value = query;
            _onAddressSearch(query);
        }
        const onAddressSelect = (option) => {
            addressQuery.value = option.label;
            // A picked suggestion replaces the address wholesale — stale country-specific
            // fields must not survive under the new street.
            form.houseNumber = "";
            form.houseNumberSuffix = "";
            form.region = "";
            applyParsedAddress(option.label, form, workspaceCountry.value, option.meta?.country ?? (form.country || defaultCountry.value), addressFormatter.names);
            if (option.meta?.city)
                form.city = option.meta.city;
            if (option.meta?.postalCode)
                form.postalCode = option.meta.postalCode;
            addressReplaced.value = true;
            pinnedPoint.value = pinSuggestionPoint(option.meta, form);
        };
        // ---------------------------------------------------------------------------
        // Address details sub-modal
        // ---------------------------------------------------------------------------
        const showAddressDetailsModal = ref(false);
        const addressDetails = reactive({
            line1: "",
            line2: "",
            line3: "",
            houseNumber: "",
            houseNumberSuffix: "",
            postalCode: "",
            city: "",
            region: "",
            country: defaultCountry.value,
        });
        const detailsShowRequiredErrors = ref(false);
        const openAddressDetailsModal = (showRequiredErrors = false) => {
            detailsShowRequiredErrors.value = showRequiredErrors;
            Object.assign(addressDetails, seedAddressDetails(form, parseAddressQuery(addressQuery.value, addressFormatter.names), defaultCountry.value, workspaceCountry.value));
            showAddressDetailsModal.value = true;
        };
        const saveAddressDetails = (details) => {
            form.line1 = details.line1;
            form.line2 = details.line2;
            form.line3 = details.line3;
            form.houseNumber = details.houseNumber;
            form.houseNumberSuffix = details.houseNumberSuffix;
            form.postalCode = details.postalCode;
            form.city = details.city;
            form.region = details.region;
            form.country = details.country;
            addressQuery.value = addressFormatter.format(form);
            addressReplaced.value = true;
        };
        // ---------------------------------------------------------------------------
        // Derived validity
        // ---------------------------------------------------------------------------
        const effectiveLine1 = computed(() => {
            if (form.line1.trim())
                return form.line1.trim();
            return parseAddressQuery(addressQuery.value, addressFormatter.names)?.line1?.trim() ?? "";
        });
        const hasResolvableAddress = computed(() => addressQuery.value.trim().length > 0 && effectiveLine1.value.length > 0);
        const canSave = computed(() => hasResolvableAddress.value);
        // ---------------------------------------------------------------------------
        // Submit
        // ---------------------------------------------------------------------------
        const handleSave = async () => {
            if (!canSave.value || isSaving.value)
                return;
            isSaving.value = true;
            try {
                const queryMatchesSeeded = addressQuery.value === seededAddressQuery.value;
                const hasStructured = !!(form.line1 || form.houseNumber || form.postalCode || form.city);
                if (!hasStructured || (!addressReplaced.value && !queryMatchesSeeded)) {
                    // Free-typed text describes a NEW address: parse its French-shaped fields
                    // and drop the granular fields that belonged to the previous one.
                    form.houseNumber = "";
                    form.houseNumberSuffix = "";
                    form.region = "";
                    applyParsedAddress(addressQuery.value, form, workspaceCountry.value, form.country || defaultCountry.value, addressFormatter.names);
                }
                // Country-specific fields ride on the form: seeded from the stored address,
                // cleared by a wholesale suggestion replace, edited via the details modal.
                const layout = workspaceCountry.value;
                const saved = toAddressLayout(form, layout);
                // line1 alone is not enough in the NL layout: its house number is required too.
                if (!requiredAddressFieldsFilled(saved, layout)) {
                    openAddressDetailsModal(true);
                    return;
                }
                const point = pointForFields(pinnedPoint.value, form);
                await updateContactAddress(props.contactId, props.initialAddress.id, {
                    type: selectedType.value,
                    label: props.initialAddress.label,
                    addressLine: saved.line1 || undefined,
                    addressLine2: saved.line2 || undefined,
                    addressLine3: saved.line3 || undefined,
                    houseNumber: saved.houseNumber || undefined,
                    houseNumberSuffix: saved.houseNumberSuffix || undefined,
                    postalCode: saved.postalCode || undefined,
                    city: saved.city || undefined,
                    region: saved.region || undefined,
                    country: saved.country || defaultCountry.value || undefined,
                    latitude: point?.latitude,
                    longitude: point?.longitude,
                    isPrimary: isPrimary.value,
                });
                emit("updated");
                emit("update:modelValue", false);
                toastSuccess("contact.detail.addresses.editModal.successToast");
            }
            catch (e) {
                const code = getApiErrorCode(e);
                const key = code && ERROR_OVERRIDES[code]
                    ? ERROR_OVERRIDES[code]
                    : "contact.detail.addresses.editModal.errorToast";
                toastError(key);
            }
            finally {
                isSaving.value = false;
            }
        };
        watch(() => props.modelValue, (visible) => {
            if (visible)
                resetForm();
        }, { immediate: true });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    "model-value": __props.modelValue,
                    size: "md",
                    "show-close-button": true,
                    "footer-position": "right",
                    "close-on-escape": !showAddressDetailsModal.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => (_ctx.$emit('update:modelValue', $event)))
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h2", _hoisted_1, [
                            _cache[8] || (_cache[8] = _createElementVNode("span", {
                                class: "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-(--border-border-base-soft) bg-white",
                                "aria-hidden": "true"
                            }, [
                                _createElementVNode("i", { class: "ri-map-pin-2-line text-xl text-(--text-text-black-default)" })
                            ], -1 /* CACHED */)),
                            _createTextVNode(" " + _toDisplayString(_ctx.$t("contact.detail.addresses.editModal.title")), 1 /* TEXT */)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(_unref(Button), {
                                variant: "error-secondary",
                                label: _ctx.$t('contact.detail.addresses.editModal.delete'),
                                "icon-left": "ri-delete-bin-line",
                                onClick: _cache[4] || (_cache[4] = ($event) => (_ctx.$emit('delete')))
                            }, null, 8 /* PROPS */, ["label"]),
                            _createElementVNode("div", _hoisted_4, [
                                _createVNode(_unref(Button), {
                                    variant: "secondary",
                                    label: _ctx.$t('contact.detail.addresses.editModal.cancel'),
                                    onClick: _cache[5] || (_cache[5] = ($event) => (_ctx.$emit('update:modelValue', false)))
                                }, null, 8 /* PROPS */, ["label"]),
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    label: _ctx.$t('contact.detail.addresses.editModal.save'),
                                    disabled: !canSave.value || isSaving.value,
                                    loading: isSaving.value,
                                    onClick: handleSave
                                }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                            ])
                        ])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                modelValue: addressQuery.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((addressQuery).value = $event)),
                                label: _ctx.$t('contact.detail.addresses.formModal.address.label'),
                                placeholder: _ctx.$t('contact.detail.addresses.formModal.address.placeholder'),
                                required: true,
                                options: _unref(addressSuggestions),
                                loading: _unref(addressLoading),
                                "icon-left": "ri-map-pin-2-line",
                                "show-add-more-details": "",
                                "add-more-details-label": _ctx.$t('contact.detail.addresses.formModal.address.addDetails'),
                                "keep-input-on-edit": "",
                                "full-width": "",
                                onSearch: onAddressSearch,
                                onSelect: onAddressSelect,
                                onAddMoreDetails: _cache[1] || (_cache[1] = ($event) => (openAddressDetailsModal()))
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "add-more-details-label"]),
                            _createVNode(_unref(SelectInput), {
                                modelValue: selectedType.value,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((selectedType).value = $event)),
                                label: _ctx.$t('contact.detail.addresses.formModal.type.label'),
                                options: typeOptions.value,
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["modelValue", "label", "options"]),
                            _createVNode(_unref(ToggleInput), {
                                modelValue: isPrimary.value,
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((isPrimary).value = $event)),
                                type: "default",
                                label: _ctx.$t('contact.detail.addresses.formModal.setAsPrimary'),
                                disabled: props.initialAddress.isPrimary
                            }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["model-value", "close-on-escape"]),
                _createVNode(AddressDetailsModal, {
                    modelValue: showAddressDetailsModal.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((showAddressDetailsModal).value = $event)),
                    "initial-details": addressDetails,
                    "show-required-errors": detailsShowRequiredErrors.value,
                    onSave: saveAddressDetails
                }, null, 8 /* PROPS */, ["modelValue", "initial-details", "show-required-errors"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
